import {useState, type FormEvent} from 'react'
import {Navigate, useParams, useSearchParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {useMutation, useQueryClient} from '@tanstack/react-query'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger} from '@/components/ui/dialog'
import {Button} from '@/components/ui/button'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Tabs, TabsContent, TabsList, TabsTrigger} from '@/components/ui/tabs'
import {CiiuPicker, RiskOptionsPicker} from '@/components/ciiu-risk-picker'
import {Skeleton} from '@/components/ui/skeleton'
import {Switch} from '@/components/ui/switch'
import {Tag} from '@/components/ui/tag'
import {CatalogOtherField} from '@/components/catalog-other-field'
import {GroupHeader, HelpIcon} from '@/components/help-icon'
import {MicroLabel} from '@/components/period-selector'
import {NoAccessState} from '@/components/no-access-state'
import {SectionCard} from '@/components/section-card'
import {PlanLockedPage} from '@/components/plan-upsell'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {FieldError} from '@/components/field-error'
import {PhotoUploadField} from '@/components/photo-upload-field'
import {FactsSection} from '@/components/perfil/facts-section'
import {HazardsSection} from '@/components/perfil/hazards-section'
import {SedesSection} from '@/components/perfil/sedes-section'
import {AreasSection} from '@/components/perfil/areas-section'
import {RegimeExpandedStamp} from '@/components/regime-expanded-stamp'
import {RegistrationLinkCard} from '@/components/registration-link-card'
import {
  useBusiness,
  useCapabilities,
  useRemoveBusinessLogo,
  useUploadBusinessLogo,
  type Business,
  type BusinessDetail,
} from '@/hooks/empresa/organizacion/use-businesses'
import {useGodModeReadOnly} from '@/hooks/platform/use-god-mode'
import {useRiskOptions, type CiiuCode} from '@/hooks/common/referencias/use-ciiu'
import {useAllEmployees} from '@/hooks/empresa/identidad/use-employees'
import {useSgsst, CHAPTER_STANDARD_COUNTS, previewChapter} from '@/hooks/sgsst/evaluacion/use-sgsst'
import {api} from '@/lib/api'
import {track} from '@/lib/telemetry'
import {translateApiError} from '@/lib/errors'
import {useUpgradeGate} from '@/components/billing/use-upgrade-gate'
import {RISK_CLASS_ROMAN, RISK_CLASS_TONE, formatRatePct} from '@/lib/risk'
import {validateRequired} from '@/lib/validation'
import {tope} from '@/lib/campos-texto'

// Espejo de ARL_OPTIONS del backend (app/core/validation.py).
const ARL_OPTIONS = [
  'Positiva',
  'Sura',
  'Colmena',
  'Bolívar',
  'AXA Colpatria',
  'La Equidad',
  'Liberty',
  'Mapfre',
  'Previsora',
  'Alfa',
] as const

// Paso en línea y no un modal anidado: dos diálogos de Radix apilados dejan pegado el bloqueo de scroll.
function RegimeConsequencesPanel({
  onConfirm,
  onCancel,
  pending,
  ciiuChanged,
  assessment,
  wouldBeChapter,
}: {
  onConfirm: () => void
  onCancel: () => void
  pending: boolean
  ciiuChanged: boolean
  assessment: {chapter: number; worker_count: number; period_year: number} | null
  wouldBeChapter: number | null
}) {
  const {t} = useTranslation()
  return (
    <div className="space-y-4" data-testid="regime-consequences">
      <div className="space-y-4 text-sm">
        <div className="space-y-1.5">
          <MicroLabel>{t('business.consequences.current')}</MicroLabel>
          {assessment ? (
            <p className="text-foreground">
              {t('business.consequences.regimeLine', {
                chapter: assessment.chapter,
                standards: CHAPTER_STANDARD_COUNTS[assessment.chapter],
                workers: assessment.worker_count,
              })}
            </p>
          ) : (
            <p className="text-muted-foreground">{t('business.consequences.noAssessment')}</p>
          )}
        </div>
        <div className="space-y-1.5">
          <MicroLabel>{t('business.consequences.wouldBe')}</MicroLabel>
          {wouldBeChapter !== null ? (
            <p className="text-foreground">
              {t('business.consequences.regimeLineShort', {
                chapter: wouldBeChapter,
                standards: CHAPTER_STANDARD_COUNTS[wouldBeChapter],
              })}
            </p>
          ) : (
            <p className="text-muted-foreground">{t('business.consequences.wouldBePending')}</p>
          )}
        </div>
        {assessment && (
          <p className="text-muted-foreground">
            {t('business.consequences.timing', {
              year: assessment.period_year,
              next: assessment.period_year + 1,
            })}
          </p>
        )}
        {ciiuChanged && (
          <p className="tone-amber rounded-lg px-3 py-2" data-testid="regime-ciiu-clears">
            {t('business.consequences.ciiuClears')}
          </p>
        )}
      </div>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel}>
          {t('business.consequences.cancel')}
        </Button>
        <Button type="button" disabled={pending} onClick={onConfirm} data-testid="regime-confirm">
          {t('business.consequences.confirm')}
        </Button>
      </DialogFooter>
    </div>
  )
}

function EditBusinessDialog({
  business,
  open,
  onOpenChange,
}: {
  business: Business
  /** Controlado por la página: Radix desmonta el `TabsContent` y un efecto de montaje lo reabría solo. */
  open: boolean
  onOpenChange: (next: boolean) => void
}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const {data: employees} = useAllEmployees(business.id)
  const {data: sgsstState} = useSgsst(business.id)
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [formName, setFormName] = useState(business.name)
  const [nameError, setNameError] = useState<string | undefined>(undefined)
  const [legalRepId, setLegalRepId] = useState(business.legal_representative_id ?? '')
  const [ciiu, setCiiu] = useState<CiiuCode | null>(null)
  const [riskId, setRiskId] = useState('')
  const [arl, setArl] = useState(business.arl ?? '')
  const [error, setError] = useState<string | null>(null)
  const upgradeGate = useUpgradeGate(business.id, 'riesgo', (band) =>
    setError(t('planFacturacion.upgradeModal.retryNote', {band}))
  )
  const {data: riskOptions} = useRiskOptions(ciiu?.code ?? null)

  const assessment = sgsstState?.assessment
    ? {
        chapter: sgsstState.assessment.chapter,
        worker_count: sgsstState.assessment.worker_count,
        period_year: sgsstState.assessment.period_year,
      }
    : null
  const ciiuChanged = (ciiu?.code ?? null) !== (business.ciiu_code ?? null)
  const riskChanged = (riskId || null) !== (business.risk_classification_id ?? null)
  const selectedRiskClass = riskOptions?.find((option) => option.id === riskId)?.risk_class
  // Un cambio de CIIU borra el riesgo, así que el capítulo nuevo todavía no se puede derivar.
  const wouldBeChapter =
    !ciiuChanged && assessment && selectedRiskClass !== undefined
      ? previewChapter(assessment.worker_count, selectedRiskClass)
      : null

  function handleOpenChange(next: boolean) {
    onOpenChange(next)
    setError(null)
    setConfirmOpen(false)
    if (next) {
      setFormName(business.name)
      setNameError(undefined)
      setLegalRepId(business.legal_representative_id ?? '')
      setCiiu(business.ciiu_code ? {code: business.ciiu_code, description: business.ciiu_description ?? ''} : null)
      setRiskId(business.risk_classification_id ?? '')
      setArl(business.arl ?? '')
    }
  }

  // La subactividad anterior pertenece a otra actividad.
  function handleCiiuChange(next: CiiuCode | null) {
    setCiiu(next)
    setRiskId('')
  }

  const mutation = useMutation({
    mutationFn: async () =>
      api.patch(`/businesses/${business.id}`, {
        name: formName,
        legal_representative_id: legalRepId || null,
        ciiu_code: ciiu?.code ?? null,
        risk_classification_id: riskId || null,
        arl: arl || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['business', business.id]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
      queryClient.invalidateQueries({queryKey: ['sgsst', business.id]})
      if (riskChanged && riskId) track('risk_assigned', {business_id: business.id})
      handleOpenChange(false)
    },
    onError: (err) => {
      // Cruzar a la banda paga responde 409 antes de escribir nada: se abre el modal de upgrade.
      if (upgradeGate.catchGate(err, {proposed_risk_classification_id: riskId || null})) {
        setConfirmOpen(false)
        return
      }
      setError(translateApiError(err))
    },
  })

  function handleSubmit(e: FormEvent) {
    e.preventDefault()
    const problem = validateRequired(formName)
    setNameError(problem ?? undefined)
    if (problem) return
    // Las consecuencias solo se muestran si ya hay un régimen fijado que afectar.
    if (assessment && (ciiuChanged || riskChanged)) {
      setConfirmOpen(true)
      return
    }
    mutation.mutate()
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline" data-testid="nav-riesgo">
          {t('business.edit')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{confirmOpen ? t('business.consequences.title') : t('business.editTitle')}</DialogTitle>
        </DialogHeader>
        {confirmOpen ? (
          <RegimeConsequencesPanel
            onConfirm={() => mutation.mutate()}
            onCancel={() => handleOpenChange(false)}
            pending={mutation.isPending}
            ciiuChanged={ciiuChanged}
            assessment={assessment}
            wouldBeChapter={wouldBeChapter}
          />
        ) : (
          <form onSubmit={handleSubmit} noValidate className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">{t('business.name')}</Label>
              <Input
                maxLength={tope('NOM')}
                id="name"
                value={formName}
                onChange={(e) => {
                  setFormName(e.target.value)
                  setNameError(undefined)
                }}
                onBlur={() => setNameError(validateRequired(formName) ?? undefined)}
                aria-invalid={!!nameError}
                required
              />
              <FieldError error={nameError} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="legal_rep">{t('business.legalRepresentative')}</Label>
              <select
                id="legal_rep"
                value={legalRepId}
                onChange={(e) => setLegalRepId(e.target.value)}
                className="h-9 w-full rounded-lg border border-input bg-transparent px-2.5 text-sm text-foreground">
                <option value="">{t('business.selectLegalRepresentative')}</option>
                {employees?.map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} {employee.last_name} ({employee.cedula})
                  </option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="ciiu_trigger">{t('business.ciiu.label')}</Label>
              <CiiuPicker ciiu={ciiu} onChange={handleCiiuChange} />
              {ciiuChanged && business.risk_classification_id && (
                <p className="tone-amber rounded-lg px-3 py-2 text-xs" data-testid="ciiu-change-risk-warning">
                  {t('business.consequences.ciiuClears')}
                </p>
              )}
            </div>
            {ciiu && (
              <div className="space-y-2">
                <Label>{t('business.risk.optionsLabel')}</Label>
                <RiskOptionsPicker ciiuCode={ciiu.code} riskId={riskId} onPick={setRiskId} />
              </div>
            )}
            <CatalogOtherField
              id="business-arl"
              label={t('business.arl.label')}
              options={ARL_OPTIONS}
              value={arl}
              onChange={setArl}
              onCommit={setArl}
            />
            <FormDialogFooter
              error={error}
              pending={mutation.isPending}
              submitLabel={t('business.save')}
              onCancel={() => handleOpenChange(false)}
            />
          </form>
        )}
      </DialogContent>
      {upgradeGate.modal}
    </Dialog>
  )
}

// Solo el dueño decide si la IA revisa lo que aportan los empleados; lo propio se elige por archivo.
function AiValidationSetting({business, canEdit}: {business: Business; canEdit: boolean}) {
  const {t} = useTranslation()
  const queryClient = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const mutation = useMutation({
    mutationFn: async (next: boolean) => api.patch(`/businesses/${business.id}`, {ai_validate_aportes: next}),
    onSuccess: (_data, next) => {
      queryClient.invalidateQueries({queryKey: ['business', business.id]})
      queryClient.invalidateQueries({queryKey: ['businesses']})
      track('ai_toggle_used', {business_id: business.id, enabled: next})
    },
    onError: (err) => setError(translateApiError(err)),
  })

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-1.5">
          {t('validation.business.label')}
          <HelpIcon
            label={t('common.help.iconLabelFor', {campo: t('validation.business.label')})}
            testId="ai-business-tip">
            <span className="block">{t('validation.business.explainer')}</span>
            <span className="mt-1 block">{t('validation.business.perUploadNote')}</span>
          </HelpIcon>
        </span>
      }
      bodyClassName="space-y-2 p-5"
      actions={
        <Switch
          checked={business?.ai_validate_aportes ?? false}
          disabled={!canEdit || mutation.isPending}
          onCheckedChange={(next) => {
            setError(null)
            mutation.mutate(next)
          }}
          data-testid="ai-business-toggle"
          aria-label={t('validation.business.label')}
        />
      }>
      {error && <p className="text-xs text-destructive">{error}</p>}
    </SectionCard>
  )
}

// Logo del tenant para el encabezado del export IPEVR, nunca el de Inci. PhotoUploadField valida
// formato y tamaño con la misma regla que ALLOWED_LOGO_CONTENT_TYPES/MAX_EVIDENCE_BYTES del backend.
function LogoSection({business, canEdit}: {business: BusinessDetail; canEdit: boolean}) {
  const {t} = useTranslation()
  const upload = useUploadBusinessLogo(business.id)
  const remove = useRemoveBusinessLogo(business.id)

  return (
    <SectionCard
      title={
        <span className="inline-flex items-center gap-1.5">
          {t('business.logo.label')}
          <HelpIcon label={t('common.help.iconLabelFor', {campo: t('business.logo.label')})} testId="logo-help">
            {t('business.logo.help')}
          </HelpIcon>
        </span>
      }
      bodyClassName="space-y-3 p-5">
      {business.logo_url ? (
        <div className="flex items-center gap-4">
          <img
            src={business.logo_url}
            alt={t('business.logo.label')}
            className="h-16 max-w-[220px] rounded-lg border border-border object-contain p-1"
            data-testid="business-logo-preview"
          />
          {canEdit && (
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={remove.isPending}
              onClick={() => remove.mutate()}
              data-testid="business-logo-remove">
              {t('business.logo.remove')}
            </Button>
          )}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="business-logo-empty">
          {t('business.logo.empty')}
        </p>
      )}
      {canEdit && (
        <PhotoUploadField
          testId="business-logo-upload"
          buttonLabel={business.logo_url ? t('business.logo.replace') : t('business.logo.upload')}
          listAttached={false}
          onUpload={(file, onProgress) => upload.mutateAsync({file, onProgress})}
        />
      )}
    </SectionCard>
  )
}

type TabKey = 'datos' | 'riesgo' | 'ajustes' | 'sedes' | 'areas'

/** `parseTab` filtra por lo que ve quien mira: sin eso, `?tab=ajustes` sin `sgsst_operar` dejaba el panel vacío. */
const TABS_SIEMPRE: TabKey[] = ['datos', 'sedes', 'areas']
const TABS_OPERADOR: TabKey[] = ['datos', 'riesgo', 'ajustes', 'sedes', 'areas']

function parseTab(value: string | null, canOperate: boolean): TabKey | null {
  const visibles = canOperate ? TABS_OPERADOR : TABS_SIEMPRE
  return visibles.includes(value as TabKey) ? (value as TabKey) : null
}

export function BusinessProfilePage() {
  const {t} = useTranslation()
  const {businessId} = useParams()
  const {data: business, isLoading} = useBusiness(businessId)
  const godReadOnly = useGodModeReadOnly()
  const isOwner = business?.role === 'owner'
  const capabilities = useCapabilities(businessId)
  const canOperate = capabilities.sgsst_operar
  // Solo el dueño edita el perfil de empresa porque mueve compuertas legales; un responsable lo ve en lectura.
  const canEdit = isOwner && !godReadOnly
  const canEditCatalogo = canOperate && !godReadOnly
  const {data: sgsstState} = useSgsst(canOperate ? businessId : undefined)
  const openAssessment = sgsstState?.assessment ?? null
  // El diálogo vive en «Datos» y uno de sus CTA está fuera: la apertura es estado de la página.
  const [editOpen, setEditOpen] = useState(false)
  const [searchParams, setSearchParams] = useSearchParams()
  const tab = parseTab(searchParams.get('tab'), canOperate) ?? 'datos'
  function setTab(next: TabKey) {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set('tab', next)
      return params
    })
  }

  // Los enlaces guardados a `?tab=organigrama` redirigen a su página propia.
  if (searchParams.get('tab') === 'organigrama' && businessId) {
    return <Navigate to={`/businesses/${businessId}/organigrama`} replace />
  }

  if (isLoading) return <Skeleton className="h-40 w-full" />
  if (!business) return <NoAccessState />

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-heading text-xl text-foreground">{t('business.profileTitle')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{business.name}</p>
          {openAssessment && (
            <div className="mt-2">
              <RegimeExpandedStamp
                chapter={openAssessment.chapter}
                prevChapter={sgsstState!.periods.find((p) => p.year === openAssessment.period_year - 1)?.chapter}
              />
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {isOwner && (
            <RegistrationLinkCard
              businessId={business.id}
              canEdit={canEdit}
              // El diálogo solo está montado en «Datos»: primero se cambia de pestaña y después se abre.
              onEditProfile={
                canEdit
                  ? () => {
                      setTab('datos')
                      setEditOpen(true)
                    }
                  : undefined
              }
            />
          )}
        </div>
      </div>

      <Tabs value={tab} onValueChange={(v) => setTab(v as TabKey)}>
        <TabsList className="flex-wrap gap-x-4 gap-y-1">
          <TabsTrigger value="datos" data-testid="tab-datos">
            {t('business.tabs.datos')}
          </TabsTrigger>
          {canOperate && (
            <TabsTrigger value="riesgo" data-testid="tab-riesgo">
              {t('business.tabs.riesgo')}
            </TabsTrigger>
          )}
          {canOperate && (
            <TabsTrigger value="ajustes" data-testid="tab-ajustes">
              {t('business.tabs.ajustes')}
            </TabsTrigger>
          )}
          <TabsTrigger value="sedes" data-testid="tab-sedes">
            {t('business.tabs.sedes')}
          </TabsTrigger>
          <TabsTrigger value="areas" data-testid="tab-areas">
            {t('business.tabs.areas')}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="datos" className="space-y-6 pt-4">
          {/* Tres disparadores, uno por grupo: excepción que docs/doctrina/ux-doctrina-en-codigo.md § «Excepciones vigentes»
              reserva a este archivo, porque CIIU, clase de riesgo y ARL son normas distintas. */}
          <SectionCard
            title={t('business.identificacionTitle')}
            actions={
              canEdit ? (
                <EditBusinessDialog business={business} open={editOpen} onOpenChange={setEditOpen} />
              ) : undefined
            }
            bodyClassName="grid grid-cols-1 gap-5 p-5 text-sm sm:grid-cols-2">
            <div className="space-y-1.5">
              <MicroLabel>{t('business.nit')}</MicroLabel>
              <p className="font-mono text-foreground">{business.nit}</p>
            </div>
            <div className="space-y-1.5">
              <GroupHeader label={t('business.ciiuCode')} help={t('business.ciiu.help')} testId="ciiu-help" />
              <p className="font-mono text-foreground">{business.ciiu_code ?? t('business.notSet')}</p>
              {business.ciiu_description && (
                <p className="line-clamp-2 text-xs text-muted-foreground">{business.ciiu_description}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <GroupHeader label={t('business.risk.label')} help={t('business.risk.help')} testId="risk-help" />
              {business.risk_classification ? (
                <>
                  <p className="font-mono text-foreground">
                    <Tag tone={RISK_CLASS_TONE[business.risk_classification.risk_class]} className="mr-1 align-middle">
                      {t('business.risk.classValue', {
                        roman: RISK_CLASS_ROMAN[business.risk_classification.risk_class],
                      })}
                    </Tag>{' '}
                    · {formatRatePct(business.risk_classification.initial_rate_pct)}
                  </p>
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {business.risk_classification.description}
                  </p>
                </>
              ) : (
                <p className="font-mono text-foreground">{t('business.risk.notClassified')}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <GroupHeader label={t('business.arl.label')} help={t('business.arl.help')} testId="arl-help" />
              {business.arl ? (
                <p className="font-mono text-foreground">{business.arl}</p>
              ) : (
                <div className="flex flex-wrap items-center gap-2" data-testid="arl-pending">
                  <Tag tone="amber">{t('business.arl.pending')}</Tag>
                  {canEdit ? (
                    <button
                      type="button"
                      data-testid="arl-assign-cta"
                      onClick={() => setEditOpen(true)}
                      className="text-xs font-medium text-primary underline decoration-dotted underline-offset-2 hover:no-underline">
                      {t('business.arl.assignCta')}
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </SectionCard>
        </TabsContent>

        <TabsContent value="riesgo" className="space-y-6 pt-4">
          {canOperate && (
            <div className="space-y-6" data-testid="perfil-riesgo-fold">
              <FactsSection businessId={business.id} canEdit={canEdit} />
              <HazardsSection businessId={business.id} canEdit={canEdit} />
            </div>
          )}
        </TabsContent>

        <TabsContent value="ajustes" className="space-y-6 pt-4">
          {canOperate && <LogoSection business={business} canEdit={canEdit} />}
          {canOperate && <AiValidationSetting business={business} canEdit={canEdit} />}
        </TabsContent>

        {(['sedes', 'areas'] as const).map((key) => (
          <TabsContent key={key} value={key} className="pt-4">
            {canOperate &&
              (capabilities.can_use_tags ? (
                key === 'sedes' ? (
                  <SedesSection businessId={business.id} canEdit={canEditCatalogo} />
                ) : (
                  <AreasSection businessId={business.id} canEdit={canEditCatalogo} />
                )
              ) : (
                <PlanLockedPage
                  feature={t('plans.feature.tags')}
                  businessId={business.id}
                  businessName={business.name}
                />
              ))}
          </TabsContent>
        ))}
      </Tabs>
    </div>
  )
}
